import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';

import {
  atomActionType,
  atomFiltersAction,
  atomHistorySearchPagerActions,
  atomPagerAction,
} from '@/modules/CampaignsModule/atoms/actionsAtom';
import { atomInfoCampaignV1 } from '@/modules/CampaignsModule/atoms/campaignAtom';
import { atomCreateActionsModalIsOpen } from '@/modules/CampaignsModule/atoms/createActionsAtom';
import { defaultPager, WHATSAPP_PREFIX } from '@/modules/CampaignsModule/constants';
import { useActionsFilters } from '@/modules/CampaignsModule/hooks/useActionsFilters';
import { useActionTableUtils } from '@/modules/CampaignsModule/hooks/useActionTableUtils';
import {
  EChannelsID,
  ITableCampaignActions,
} from '@/modules/CampaignsModule/interfaces/CampaignActions';
import { ITablePager } from '@/modules/CampaignsModule/interfaces/Campaigns';
import { getLocationAction } from '@/modules/CampaignsModule/utils';

export const useHandleEvents = () => {
  const navigate = useNavigate();
  const createSimpleEmail = useFeatureFlag('createSimpleEmail');
  const createSimpleSMS = useFeatureFlag('createSimpleSMS');
  const enableWhatsAppModule = useFeatureFlag('enableWhatsAppModule');

  const filtersAction = useAtomValue(atomFiltersAction);
  const pager = useAtomValue(atomPagerAction);
  const actionType = useAtomValue(atomActionType);

  const setInfoCampaignV1 = useSetAtom(atomInfoCampaignV1);
  const setPageParams = useSetAtom(atomPagerAction);

  const setHistorySearchPagerAction = useSetAtom(atomHistorySearchPagerActions);
  const setCreateActionsModalIsOpen = useSetAtom(atomCreateActionsModalIsOpen);

  const {
    events: { getRoutePathAction },
  } = useActionTableUtils();

  const { getActions, resetFilters } = useActionsFilters();

  const handleChangeTablePage = useCallback(
    (payload?: ITablePager) => {
      const { pageIndex = 1, pageSize = 10 } = payload ?? {};
      const newLimit = pageSize;
      const newPage = pageIndex + 1;

      if (pager?.page !== newPage || pager?.limit !== newLimit) {
        setPageParams({ page: newPage, limit: newLimit });
        void getActions({
          ...filtersAction,
          page: newPage,
          limit: newLimit,
        });
      }
    },
    [filtersAction, pager, getActions, setPageParams]
  );

  const handleRowClick = useCallback(
    (row: ITableCampaignActions) => {
      const { name, channel, status, splitTest: isSplit, htmlOriginId, originId } = row.action;

      const router = getRoutePathAction({
        channel,
        htmlOriginId,
        isSplit,
        originId,
        createSimpleEmail,
      });

      setInfoCampaignV1({
        campaign: {
          id: Number(row.action.campaignId),
          name: row.action.campaignName ?? '',
          isAutomatic: channel === EChannelsID.MASTER,
        },
        action: {
          id: row.action.actionId,
          name: name,
        },
        route: actionType
          ? `/v2/campaigns/actions-${actionType}`
          : `/v2/campaigns/${row.action.campaignId}`,
      });

      setHistorySearchPagerAction({
        filters: { ...filtersAction },
        pager: {
          page: pager?.page || defaultPager.page,
          limit: pager?.limit || defaultPager.limit,
        },
      });

      const locationAction = getLocationAction({
        campaignId: row.action.campaignId,
        actionId: row.action.actionId,
        status: status,
        featureFlag: {
          email: createSimpleEmail,
          sms: createSimpleSMS,
          whatsapp: enableWhatsAppModule,
        },
        actionTypePath: name.startsWith(WHATSAPP_PREFIX) ? 'whatsapp' : router,
      });

      navigate(locationAction);
    },
    [
      createSimpleEmail,
      createSimpleSMS,
      enableWhatsAppModule,
      actionType,
      filtersAction,
      pager?.limit,
      pager?.page,
      navigate,
      getRoutePathAction,
      setHistorySearchPagerAction,
      setInfoCampaignV1,
    ]
  );

  const handleCreateActionByCampaign = useCallback(() => {
    setCreateActionsModalIsOpen(true);
  }, [setCreateActionsModalIsOpen]);

  const handleClickEmptyScreenNoResult = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  return {
    handleChangeTablePage,
    handleCreateActionByCampaign,
    handleClickEmptyScreenNoResult,
    handleRowClick,
  };
};
